// Substitutes the bare `active_id` identifier in a domain template with the
// supplied uuid string. Used by the list-drill renderer (Enhancement 06) so
// the action's domain `[('inquiry_id', '=', active_id)]` becomes
// `[('inquiry_id', '=', '<uuid>')]` before the list query fires.
//
// The substitution operates on the JSON-encoded string form (which is how
// `ir.action.domain` is stored). Bare identifier `active_id` MUST appear
// outside quoted strings and outside identifier-like tokens (it's a value
// position only, never a field name). Returns "[]" when input is empty or
// unparseable so callers see a countable empty domain rather than throwing.
//
// This is the client-side mirror of the server-side helper of the same name
// in `src/ede/foundation/presentation/models/presentations.py`. The two must
// stay in step so drilled-list rendering and the `_related-counts` endpoint
// produce identical filtered sets for the same template.

const ACTIVE_ID_TOKEN_RE = /(?<!["'\w.])active_id(?!["'\w.])/g;

// `$active_record.<field>` — references a field on the parent record at
// substitution-time. Surrounding double-quotes are consumed (optional) so
// the JSON-encoded replacement value isn't wrapped back into a quoted
// string. Mirrors the server-side regex in
// `src/ede/foundation/presentation/models/presentations.py`.
const ACTIVE_RECORD_TOKEN_RE = /"?\$active_record\.([A-Za-z_][A-Za-z0-9_]*)"?/g;

export function substituteActiveId(domainTemplate: string | null | undefined, activeId: string): string {
    if (!domainTemplate || domainTemplate.trim() === "") return "[]";
    const quoted = activeId ? JSON.stringify(activeId) : '""';
    return domainTemplate.replace(ACTIVE_ID_TOKEN_RE, quoted);
}

// Normalize a parent-record field value for embedding in a JSON domain.
// Reference shape {id, display_name} → bare UUID; list of refs → list of
// bare UUIDs (M2M); scalars pass through. Mirrors `_coerce_for_domain` in
// presentations.py.
function coerceForDomain(value: unknown): unknown {
    if (value && typeof value === "object" && !Array.isArray(value)) {
        const obj = value as Record<string, unknown>;
        return obj.id ?? obj.record_uuid ?? null;
    }
    if (Array.isArray(value)) return value.map(coerceForDomain);
    return value;
}

// Substitute `$active_record.<field>` tokens with the JSON-serialized field
// value from `parentRecord`. Tokens for fields not present on the record
// substitute to `null`. Idempotent — safe to call on a string that has no
// such tokens. Mirrors `_substitute_active_id`'s $active_record branch on
// the backend; keep the two in step.
export function substituteActiveRecord(
    domainTemplate: string | null | undefined,
    parentRecord: Record<string, unknown> | null | undefined,
): string {
    if (!domainTemplate || domainTemplate.trim() === "") return "[]";
    return domainTemplate.replace(ACTIVE_RECORD_TOKEN_RE, (_match, fieldName) => {
        const raw = parentRecord ? parentRecord[fieldName] : null;
        return JSON.stringify(coerceForDomain(raw) ?? null);
    });
}

// Bare field-name references in a domain *value* position resolve to the
// current record's value for that field — the terse, implicit counterpart to
// the explicit `$active_record.<field>` token. Used by form-view relational
// pickers so `[["parent_partner_id", "=", partner_id]]` filters the candidate
// list by the value the user picked in the sibling `partner_id` field.
//
// Only bare identifiers that are ACTUAL keys on the record are substituted, so
// JSON literals (`true` / `false` / `null`) and incidental words inside quoted
// string values are left untouched. A referenced field that is unset resolves
// to `null` (via coerceForDomain), keeping the result valid JSON. The negative
// look-behind/-ahead keep quoted field names (`"parent_partner_id"`) and the
// already-substituted `$active_record.<field>` fragments out of scope.
const BARE_FIELD_TOKEN_RE = /(?<!["'\w.$])[A-Za-z_][A-Za-z0-9_]*(?!["'\w.])/g;
const JSON_LITERAL_WORDS = new Set(["true", "false", "null"]);

export function substituteRecordFields(
    domainTemplate: string | null | undefined,
    record: Record<string, unknown> | null | undefined,
): string {
    if (!domainTemplate || domainTemplate.trim() === "") return "[]";
    return domainTemplate.replace(BARE_FIELD_TOKEN_RE, (token) => {
        if (JSON_LITERAL_WORDS.has(token)) return token;
        if (!record || !(token in record)) return token;
        return JSON.stringify(coerceForDomain(record[token]) ?? null);
    });
}

// Resolve every record reference in a domain template to concrete JSON before
// it is sent to the related-records search. Runs the explicit
// `$active_record.<field>` substitution first, then the implicit bare
// field-name substitution. Returns "[]" for an empty template; idempotent on
// templates that carry no tokens.
export function substituteDomainRecordTokens(
    domainTemplate: string | null | undefined,
    record: Record<string, unknown> | null | undefined,
): string {
    return substituteRecordFields(substituteActiveRecord(domainTemplate, record), record);
}

// Parse-then-substitute when the caller has the domain as a JSON value
// rather than a string. Walks the structure and replaces any bare-string
// "active_id" leaf with the uuid string. Returns a copy.
export function substituteActiveIdInList(domain: unknown, activeId: string): unknown {
    if (Array.isArray(domain)) return domain.map((item) => substituteActiveIdInList(item, activeId));
    if (typeof domain === "string" && domain === "active_id") return activeId;
    return domain;
}
